import {
  CheckCircle2,
  CircleDot,
  Clock3,
  Inbox,
  MessageCircle,
  RefreshCw,
  Search,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { FeedbackHistoryItem, FeedbackStatus } from '@/services/feedback-api'
import { formatFeedbackDate as formatDate, STATUS_STYLES } from './feedback-status-styles'

type FeedbackHistoryProps = {
  items: FeedbackHistoryItem[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  onStartNew?: () => void
  /** Sunucu `chatAvailable` dediği satırda "Sohbeti aç" düğmesi çıkar. */
  onOpenChat?: (trackingId: string) => void
}

const STATUS_ICONS = {
  received: CircleDot,
  in_review: Search,
  resolved: CheckCircle2,
} satisfies Record<FeedbackStatus, typeof CircleDot>

export function FeedbackHistory({
  items,
  isLoading,
  isError,
  onRetry,
  onStartNew,
  onOpenChat,
}: FeedbackHistoryProps) {
  if (isLoading) {
    return (
      <div
        role="status"
        aria-busy="true"
        className="flex min-h-48 items-center justify-center gap-3 rounded-2xl border border-border bg-muted/50 p-6 text-sm text-muted-foreground"
      >
        <RefreshCw className="size-5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        <span>Bildirimlerim yükleniyor</span>
      </div>
    )
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center"
      >
        <p className="font-medium text-foreground">Bildirimlerin yüklenemedi.</p>
        <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
          Bağlantını kontrol edip yeniden deneyebilirsin.
        </p>
        <Button type="button" variant="outline" onClick={onRetry} className="min-h-11 gap-2">
          <RefreshCw className="size-4" aria-hidden="true" />
          Tekrar dene
        </Button>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border bg-muted/40 p-6 text-center">
        <span className="inline-flex size-11 items-center justify-center rounded-full bg-background text-muted-foreground">
          <Inbox className="size-5" aria-hidden="true" />
        </span>
        <div>
          <p className="font-medium text-foreground">Henüz bir bildirimin yok</p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            Yeni bir sorun veya öneri gönderdiğinde takip numaran burada görünür.
          </p>
        </div>
        {onStartNew ? (
          <Button type="button" variant="outline" onClick={onStartNew} className="min-h-11">
            Yeni bildirim oluştur
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <ol aria-label="Bildirim yaşam döngüsü" className="space-y-3">
      {items.map((item) => {
        const StatusIcon = STATUS_ICONS[item.status]
        return (
          <li
            key={item.trackingId}
            className="relative min-w-0 overflow-hidden rounded-2xl border border-border bg-card p-4 pl-14 shadow-sm"
          >
            <span
              className={cn(
                'absolute left-4 top-4 inline-flex size-8 items-center justify-center rounded-full border',
                STATUS_STYLES[item.status],
              )}
            >
              <StatusIcon className="size-4" aria-hidden="true" />
            </span>
            <span
              aria-hidden="true"
              className="absolute bottom-0 left-[1.95rem] top-12 w-px bg-border last:hidden"
            />
            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-semibold text-foreground">
                  {item.statusLabel}
                  {item.source === 'extension' ? (
                    <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      Eklenti
                    </span>
                  ) : null}
                </p>
                <p className="mt-1 break-all font-mono text-xs tabular-nums text-muted-foreground">
                  {item.trackingId}
                </p>
              </div>
              <time
                dateTime={item.updatedAt}
                className="shrink-0 text-xs tabular-nums text-muted-foreground"
              >
                {formatDate(item.updatedAt)}
              </time>
            </div>
            <p className="mt-3 flex items-start gap-2 text-sm leading-relaxed text-muted-foreground">
              <Clock3 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>{item.etaText}</span>
            </p>
            {onOpenChat && item.chatAvailable ? (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => onOpenChat(item.trackingId)}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-brand-purple/40 px-4 text-sm font-medium text-brand-purple transition-colors duration-200 hover:bg-brand-purple/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2 motion-reduce:transition-none"
                >
                  <MessageCircle className="size-4" aria-hidden="true" />
                  Sohbeti aç
                </button>
                {(item.chatUnread ?? 0) > 0 ? (
                  <span className="rounded-full bg-brand-purple px-2.5 py-1 text-xs font-semibold text-white">
                    {item.chatUnread} yeni yanıt
                  </span>
                ) : null}
              </div>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}
